import React from 'react';
import toast from 'react-hot-toast';
import {
    Activity,
    Mail,
    Phone,
    Zap,
    Building2,
    MapPin,
    Database,
    Globe,
    Check,
    X,
    RefreshCw,
    Loader2,
    CheckCircle2,
    XCircle,
    LinkIcon
} from 'lucide-react';

export default function BranchDetailsTab({
    branch,
    organization,
    users,
    migrating,
    handleRunMigration,
    handleToggleBranchStatus
}) {
    const isActive = branch?.active;

    return (
        <div className="space-y-6">
            {/* Top Grid - 3 Columns */}
            <div className="grid grid-cols-3 gap-6">
                {/* Branch Information */}
                <div className="col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="p-6 border-b border-slate-100">
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#FFE8C8] rounded-lg flex items-center justify-center">
                                <Activity size={16} className="text-[#C45500]" />
                            </div>
                            Branch Information
                        </h3>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Branch Name</label>
                                <p className="text-base font-semibold text-slate-900">{branch.name}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Branch ID</label>
                                <code className="text-sm font-mono text-[#C45500] bg-[#FFF4E6] px-3 py-1 rounded-lg inline-block">
                                    {branch.id}
                                </code>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Email</label>
                                <div className="flex items-center gap-2 text-slate-700">
                                    <Mail size={14} className="text-slate-400" />
                                    <span className="text-sm">{branch.email || 'Not provided'}</span>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Contact</label>
                                <div className="flex items-center gap-2 text-slate-700">
                                    <Phone size={14} className="text-slate-400" />
                                    <span className="text-sm">{branch.contact || 'Not provided'}</span>
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-slate-500 uppercase mb-1.5">
                                    Business Type
                                </label>
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-bold shadow-lg shadow-blue-200">
                                    <Zap size={14} />
                                    {branch.businessType || 'QSR'}
                                </span>
                            </div>
                            {branch.gstNumber && (
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">GST Number</label>
                                    <code className="text-sm font-mono text-slate-700 bg-amber-50 px-3 py-1 rounded-lg inline-block">
                                        {branch.gstNumber}
                                    </code>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick Actions Card */}
                <div className="space-y-6">
                    {/* Organization Link */}
                    <div className="bg-gradient-to-br from-[#f08804] to-[#e88b00] text-white rounded-2xl shadow-xl shadow-blue-200 overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                    <Building2 size={20} />
                                </div>
                                <div className="flex-1">
                                    <div className="text-xs text-[#FFD89E] uppercase font-bold mb-1">Parent Organization</div>
                                    <div className="text-lg font-black">{organization?.name || 'Unknown'}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status Card */}
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <h4 className="text-sm font-bold text-slate-900 mb-4">Branch Status</h4>
                        <div className="space-y-3">

                            {/* Operational / Branch Active Toggle */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-sm font-medium text-slate-700">Operational</span>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {isActive ? 'Branch is active' : 'Branch is inactive'}
                                    </p>
                                </div>
                                {/* Toggle Switch */}
                                <button
                                    onClick={handleToggleBranchStatus}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                                        isActive ? 'bg-green-500' : 'bg-slate-300'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform duration-300 ${
                                            isActive ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>

                            <div className="border-t border-slate-100" />

                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Database</span>
                                {branch.dbName ? (
                                    <CheckCircle2 size={18} className="text-green-500" />
                                ) : (
                                    <XCircle size={18} className="text-red-500" />
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-slate-600">Users Active</span>
                                <span className="text-sm font-bold text-slate-900">
                                    {users.filter(u => u.active).length}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Location & Database - 2 Column Grid */}
            <div className="grid grid-cols-2 gap-6">
                {/* Physical Location */}
                {branch.addresses?.[0] && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="p-6 border-b border-slate-100">
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                    <MapPin size={16} className="text-green-600" />
                                </div>
                                Physical Location
                            </h3>
                        </div>
                        <div className="p-6">
                            <div className="space-y-2 text-slate-700">
                                <p className="font-semibold text-slate-900">{branch.addresses[0].address1}</p>
                                {branch.addresses[0].address2 && <p>{branch.addresses[0].address2}</p>}
                                <p className="text-sm">
                                    {branch.addresses[0].city}, {branch.addresses[0].state} {branch.addresses[0].zipcode}
                                </p>
                                <p className="text-sm text-slate-500">{branch.addresses[0].country}</p>
                            </div>
                            {/* <div className="mt-4 pt-4 border-t border-slate-100">
                                <a
                                    href={`https://maps.google.com/?q=${branch.addresses[0].city},${branch.addresses[0].state}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-[#C45500] hover:text-[#C45500] font-semibold"
                                >
                                    <Globe size={14} />
                                    Open in Maps
                                </a>
                            </div> */}
                        </div>
                    </div>
                )}

                {/* Database Configuration */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="p-6 border-b border-slate-100">
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#FFE8C8] rounded-lg flex items-center justify-center">
                                <Database size={16} className="text-[#C45500]" />
                            </div>
                            Database Configuration
                        </h3>
                    </div>
                    <div className="p-6">
                        {branch.dbName ? (
                            <div className="space-y-4">
                                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
                                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                                        <Check size={24} className="text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-green-900">Connected</p>
                                        <p className="text-sm text-green-700 font-mono">{branch.dbName}</p>
                                    </div>
                                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-300" />
                                </div>

                                {branch.dbUrl && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">JDBC URL</label>
                                        <div className="relative group">
                                            <code className="block text-xs font-mono text-slate-600 bg-slate-50 p-4 rounded-xl border border-slate-200 break-all">
                                                {branch.dbUrl}
                                            </code>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(branch.dbUrl);
                                                    toast.success('Copied to clipboard');
                                                }}
                                                className="absolute top-2 right-2 p-2 bg-white border border-slate-200 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-slate-50"
                                            >
                                                <LinkIcon size={14} className="text-slate-600" />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={handleRunMigration}
                                    disabled={migrating}
                                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-300 disabled:opacity-50"
                                >
                                    {migrating ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Running Migration...
                                        </>
                                    ) : (
                                        <>
                                            <RefreshCw size={18} />
                                            Run Database Migration
                                        </>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                    <X size={32} className="text-red-500" />
                                </div>
                                <p className="text-slate-900 font-bold mb-1">No Database Configured</p>
                                <p className="text-sm text-slate-500">Configure a database to enable this branch</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}